import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react-native';
import { useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, Switch } from 'react-native';
import { Box } from '../../../components/primitives/Box';
import { Button } from '../../../components/primitives/Button';
import { Input } from '../../../components/primitives/Input';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useModal } from '../../../contexts/ModalContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import {
  useCreateJobTitleFilter,
  useCreateJobTitleSynonym,
  useDeleteJobTitleFilter,
  useDeleteJobTitleSynonym,
  useJobTitleFilterEnabled,
  useJobTitleFilters,
  useJobTitleSynonyms,
  useToggleJobTitleFilter,
  useToggleJobTitleFilterEnabled,
} from '../../../hooks/useJobTitleFilters';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { SettingsSection } from './SettingsSection';

export function JobTitleSettingsSection() {
  const { currentOrganization } = useAuth();
  const { t } = useTranslation();
  const { confirm } = useModal();
  const orgId = currentOrganization?.id;

  const { data: filterEnabled, isLoading: enabledLoading } = useJobTitleFilterEnabled(orgId);
  const toggleEnabled = useToggleJobTitleFilterEnabled();
  const { data: filters, isLoading: filtersLoading } = useJobTitleFilters(orgId);
  const { data: synonyms, isLoading: synonymsLoading } = useJobTitleSynonyms(orgId);

  const createFilter = useCreateJobTitleFilter();
  const toggleFilter = useToggleJobTitleFilter();
  const deleteFilter = useDeleteJobTitleFilter();
  const createSynonym = useCreateJobTitleSynonym();
  const deleteSynonym = useDeleteJobTitleSynonym();

  const [newTitle, setNewTitle] = useState('');
  const [newCountry, setNewCountry] = useState('');
  const [newIndustry, setNewIndustry] = useState('');
  const [synonymsExpanded, setSynonymsExpanded] = useState(false);
  const [newSynonym, setNewSynonym] = useState('');
  const [newCanonicalTitle, setNewCanonicalTitle] = useState('');

  const handleAddTitle = () => {
    if (!newTitle.trim()) return;
    createFilter.mutate(
      {
        title: newTitle.trim(),
        ...(newCountry.trim() && { country: newCountry.trim() }),
        ...(newIndustry.trim() && { industry: newIndustry.trim() }),
      },
      {
        onSuccess: () => {
          setNewTitle('');
          setNewCountry('');
          setNewIndustry('');
        },
      },
    );
  };

  const handleAddSynonym = () => {
    if (!newSynonym.trim() || !newCanonicalTitle.trim()) return;
    createSynonym.mutate(
      { synonym: newSynonym.trim(), canonicalTitle: newCanonicalTitle.trim() },
      {
        onSuccess: () => {
          setNewSynonym('');
          setNewCanonicalTitle('');
        },
      },
    );
  };

  return (
    <SettingsSection title={t('settings.jobTitles.title')}>
      <Text variant="bodySmall" color={colors.text.secondary}>
        {t('settings.jobTitles.description')}
      </Text>

      <VStack spacing="xs" style={styles.enableRow}>
        <HStack spacing="md" align="center">
          <Switch
            value={!!filterEnabled}
            onValueChange={(value) => toggleEnabled.mutate(value)}
            trackColor={{ false: colors.background.tertiary, true: colors.primary[500] }}
            thumbColor={colors.text.primary}
            disabled={enabledLoading || toggleEnabled.isPending}
          />
          <Text variant="body" color={colors.text.primary} style={styles.enableLabel}>
            {t('settings.jobTitles.enableFilter')}
          </Text>
          {toggleEnabled.isPending && (
            <ActivityIndicator size="small" color={colors.primary[400]} />
          )}
        </HStack>
        <Text variant="caption" color={colors.text.tertiary}>
          {t('settings.jobTitles.enableFilterDescription')}
        </Text>
      </VStack>

      {filtersLoading ? (
        <ActivityIndicator size="small" color={colors.primary[400]} />
      ) : (
        <VStack spacing="sm">
          {filters?.map((filter) => (
            <HStack key={filter.id} spacing="sm" align="center" style={styles.filterRow}>
              <Switch
                value={filter.isActive}
                onValueChange={(value) => toggleFilter.mutate({ id: filter.id, isActive: value })}
                trackColor={{ false: colors.background.tertiary, true: colors.primary[500] }}
                thumbColor={colors.text.primary}
                disabled={toggleFilter.isPending}
              />
              <Text variant="body" color={colors.text.primary} style={styles.filterTitle}>
                {filter.title}
              </Text>
              {(filter.country || filter.industry) && (
                <Text variant="caption" color={colors.primary[400]}>
                  {[filter.country, filter.industry].filter(Boolean).join(' / ')}
                </Text>
              )}
              <Pressable
                onPress={async () => {
                  const confirmed = await confirm({
                    title: t('settings.jobTitles.removeTitle'),
                    message: t('settings.jobTitles.removeConfirm'),
                    variant: 'destructive',
                  });
                  if (confirmed) deleteFilter.mutate(filter.id);
                }}
                hitSlop={8}
                style={styles.deleteButton}
                accessibilityLabel={t('settings.jobTitles.removeTitle')}
              >
                <Trash2 size={14} color={colors.status.error.main} strokeWidth={2} />
              </Pressable>
            </HStack>
          ))}

          {(!filters || filters.length === 0) && (
            <Text variant="bodySmall" color={colors.text.tertiary}>
              {t('settings.jobTitles.noTitles')}
            </Text>
          )}
        </VStack>
      )}

      <VStack spacing="sm">
        <HStack spacing="sm" align="end">
          <Box style={styles.inputContainer}>
            <Input
              placeholder={t('settings.jobTitles.titlePlaceholder')}
              value={newTitle}
              onChangeText={setNewTitle}
              onSubmitEditing={handleAddTitle}
            />
          </Box>
          <Button
            variant="primary"
            size="sm"
            onPress={handleAddTitle}
            loading={createFilter.isPending}
            disabled={!newTitle.trim()}
          >
            {t('settings.jobTitles.addTitle')}
          </Button>
        </HStack>
        {createFilter.isError && (
          <Text variant="caption" color={colors.status.error.main}>
            {(createFilter.error as { response?: { data?: { message?: string } } })?.response?.data
              ?.message || t('settings.jobTitles.addError')}
          </Text>
        )}
        <HStack spacing="sm">
          <Box style={styles.inputContainer}>
            <Input
              placeholder={t('settings.jobTitles.countryPlaceholder')}
              value={newCountry}
              onChangeText={setNewCountry}
            />
          </Box>
          <Box style={styles.inputContainer}>
            <Input
              placeholder={t('settings.jobTitles.industryPlaceholder')}
              value={newIndustry}
              onChangeText={setNewIndustry}
            />
          </Box>
        </HStack>
      </VStack>

      {/* Synonyms Section */}
      <Pressable
        onPress={() => setSynonymsExpanded(!synonymsExpanded)}
        style={styles.synonymsHeader}
        accessibilityRole="button"
      >
        <Text variant="body" color={colors.text.primary} style={styles.synonymsHeaderText}>
          {t('settings.jobTitles.synonyms')}
        </Text>
        <Text variant="caption" color={colors.text.tertiary} style={styles.synonymsCount}>
          {synonyms?.length ?? 0}
        </Text>
        {synonymsExpanded ? (
          <ChevronUp size={16} color={colors.text.secondary} />
        ) : (
          <ChevronDown size={16} color={colors.text.secondary} />
        )}
      </Pressable>

      {synonymsExpanded && (
        <VStack spacing="md">
          {synonymsLoading ? (
            <ActivityIndicator size="small" color={colors.primary[400]} />
          ) : (
            <VStack spacing="sm">
              {synonyms?.map((syn) => (
                <HStack key={syn.id} spacing="sm" align="center" style={styles.filterRow}>
                  <Text variant="body" color={colors.text.primary} style={styles.filterTitle}>
                    {syn.synonym}
                  </Text>
                  <Text variant="caption" color={colors.text.tertiary}>
                    → {syn.canonicalTitle}
                  </Text>
                  <Pressable
                    onPress={async () => {
                      const confirmed = await confirm({
                        title: t('settings.jobTitles.removeSynonym'),
                        message: t('settings.jobTitles.removeConfirm'),
                        variant: 'destructive',
                      });
                      if (confirmed) deleteSynonym.mutate(syn.id);
                    }}
                    hitSlop={8}
                    style={styles.deleteButton}
                    accessibilityLabel={t('settings.jobTitles.removeSynonym')}
                  >
                    <Trash2 size={14} color={colors.status.error.main} strokeWidth={2} />
                  </Pressable>
                </HStack>
              ))}

              {(!synonyms || synonyms.length === 0) && (
                <Text variant="bodySmall" color={colors.text.tertiary}>
                  {t('settings.jobTitles.noSynonyms')}
                </Text>
              )}
            </VStack>
          )}

          <VStack spacing="sm">
            <HStack spacing="sm" align="end">
              <Box style={styles.inputContainer}>
                <Input
                  placeholder={t('settings.jobTitles.synonymPlaceholder')}
                  value={newSynonym}
                  onChangeText={setNewSynonym}
                />
              </Box>
              <Box style={styles.inputContainer}>
                <Input
                  placeholder={t('settings.jobTitles.canonicalPlaceholder')}
                  value={newCanonicalTitle}
                  onChangeText={setNewCanonicalTitle}
                  onSubmitEditing={handleAddSynonym}
                />
              </Box>
            </HStack>
            <Box style={styles.addButtonRow}>
              <Button
                variant="primary"
                size="sm"
                onPress={handleAddSynonym}
                loading={createSynonym.isPending}
                disabled={!newSynonym.trim() || !newCanonicalTitle.trim()}
              >
                {t('settings.jobTitles.addSynonym')}
              </Button>
            </Box>
            {createSynonym.isError && (
              <Text variant="caption" color={colors.status.error.main}>
                {(createSynonym.error as { response?: { data?: { message?: string } } })?.response
                  ?.data?.message || t('settings.jobTitles.addError')}
              </Text>
            )}
          </VStack>
        </VStack>
      )}
    </SettingsSection>
  );
}

const styles = StyleSheet.create({
  enableRow: {
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    backgroundColor: colors.background.tertiary,
  },
  enableLabel: {
    fontWeight: '600',
    flexShrink: 1,
  },
  filterRow: {
    paddingVertical: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    backgroundColor: colors.background.card,
  },
  filterTitle: {
    flex: 1,
  },
  deleteButton: {
    padding: tokens.spacing.xs,
    ...Platform.select({
      web: { cursor: 'pointer' } as any,
    }),
  },
  inputContainer: {
    flex: 1,
  },
  synonymsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: tokens.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border.subtle,
    ...Platform.select({
      web: { cursor: 'pointer', userSelect: 'none' } as any,
    }),
  },
  synonymsHeaderText: {
    flex: 1,
    fontWeight: '600',
  },
  synonymsCount: {
    marginRight: tokens.spacing.sm,
  },
  addButtonRow: {
    alignSelf: 'flex-start',
  },
});
